import { Avatar } from "@cvpartner/design-system";
import {
  Hand,
  LifeBuoy01,
  Lock01,
  LogOut01,
} from "@cvpartner/design-system/icons/line";
import * as React from "react";
import { Button, Header, MenuTrigger, Popover } from "react-aria-components";
import { useLoggedInUser } from "@/shared/queries/CurrentUser.tsx";
import { useWillBeLoggedOut } from "@/shared/queries/loggedInStatus.ts";
import { usePrivacyNotice } from "@/shared/queries/privacyNotice.ts";
import { getCsrfToken } from "@/shared/util/fetch.tsx";
import { I18n } from "@/shared/util/translations.ts";
import { Chevrons, Menu, MenuWrapper, NavLink, Separator } from "./MenuNav.tsx";
import { button, headerWrapper, name, roles } from "./UserNav.css.ts";

const UserThumbnail: React.FC<{
  isOpen: boolean;
  isHovered: boolean;
}> = ({ isOpen, isHovered }) => {
  const user = useLoggedInUser();
  const thumbnail = user.image?.small_thumb.url;
  const active = isOpen || isHovered;

  if (!thumbnail) {
    return <Avatar active={active} isInteractive={true} />;
  }

  return (
    <Avatar
      src={thumbnail}
      alt={user.name}
      active={active}
      isInteractive={true}
    />
  );
};

// A real form submit rather than a fetch: an ADFS or federation tenant answers with a redirect to
// the IdP, and only a navigation can follow that.
const SignOutForm: React.FC<{ ref: React.Ref<HTMLFormElement> }> = ({
  ref,
}) => (
  <form ref={ref} action="/session/logout" method="POST" hidden>
    <input
      type="hidden"
      name="authenticity_token"
      value={getCsrfToken()}
      autoComplete="off"
    />
  </form>
);

export const UserNav: React.FC = () => {
  const user = useLoggedInUser();

  const [isOpen, setIsOpen] = React.useState(false);
  const privacyNotice = usePrivacyNotice();

  const openMenu = React.useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeMenu = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const signOutRef = React.useRef<HTMLFormElement>(null);

  const submitSignOut = () => {
    signOutRef.current?.requestSubmit();
  };

  const willBeLoggedOut = useWillBeLoggedOut();

  React.useEffect(() => {
    if (willBeLoggedOut) {
      closeMenu();
    }
  }, [closeMenu, willBeLoggedOut]);

  const menu = (
    <MenuTrigger>
      <Button onPress={openMenu} className={button} aria-expanded={isOpen}>
        {({ isHovered }) => (
          <>
            <UserThumbnail isOpen={isOpen} isHovered={isHovered} />
            <Chevrons isOpen={isOpen} isHovered={isHovered} />
          </>
        )}
      </Button>
      <Popover
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        placement="bottom right"
      >
        <Menu>
          <Header className={headerWrapper}>
            <div className={name}>{user.name}</div>
            <div className={roles}>{user.displayableRolesUserNames}</div>
          </Header>
          {privacyNotice?.mustAgree ? null : (
            <>
              {user.is_authenticated_by_password ? (
                <>
                  <NavLink
                    href="/change_password"
                    icon={Lock01}
                    text={I18n.t("change_password")}
                  />
                  <Separator />
                </>
              ) : null}
              {privacyNotice?.enabled && (
                <>
                  <NavLink
                    href="/privacy-notice-agreement"
                    icon={Hand}
                    text={I18n.t("privacy_notice_heading")}
                    onAction={closeMenu}
                  />
                  <Separator />
                </>
              )}
              {user.role !== "external" && (
                <>
                  <NavLink
                    href="/settings#/help_guides"
                    icon={LifeBuoy01}
                    text={I18n.t("settings_labels.guides_and_learning")}
                    onAction={closeMenu}
                  />
                  <Separator />
                </>
              )}
            </>
          )}
          <NavLink
            icon={LogOut01}
            text={I18n.t("nav.user.sign_out")}
            onAction={submitSignOut}
          />
        </Menu>
      </Popover>
    </MenuTrigger>
  );

  return (
    <>
      {isOpen ? (
        menu
      ) : (
        <MenuWrapper isOpen={false} open={openMenu}>
          {menu}
        </MenuWrapper>
      )}
      <SignOutForm ref={signOutRef} />
    </>
  );
};
